"use client";

import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * Banner 1051 — KUK home hero.
 *
 * Port of kuk-campus-portal `components/Home/Hero.tsx` over
 * `components/organisms/Banner.tsx`: a full-bleed slide carousel with a
 * translucent white card pinned to its left, carrying a small "Welcome to"
 * eyebrow, the university name, a coloured "Online" line, a paragraph of copy
 * and a single CTA.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px and `lg:` is 1024px. This app remaps `md:` to 1230px and exposes
 * 768px as `tab:` (see globals.css `--breakpoint-*`), so the reference's `md:`
 * is ported as `tab:` and its `lg:` as this app's `sm:` (1080px) — the nearest
 * real breakpoint, and the one this app already uses for "desktop starts here".
 *
 * SECTION HEIGHT — v1 measures the viewport in JS and picks `90vh` under an
 * 800px-tall window, `75vh` otherwise, with a flat `60vh` below 768px. That is
 * reproduced in pure CSS (a `max-height` media query), so it is correct on the
 * server render instead of only after the first client measurement. Setting
 * `hero_section_height` pins the desktop height to that many pixels instead;
 * leaving it at 0 keeps v1's viewport-relative behaviour.
 *
 * NOT CARRIED OVER — the promotional marquee v1 renders above the carousel from
 * inside this same component. This app already treats that strip as its own
 * page section (Header 5016), so it can be reordered, reworded or hidden
 * without touching the hero.
 *
 * NOT CARRIED OVER — v1's `isUserVerified` branch, which swaps the CTA for a
 * "Continue Application" link to the admission portal. That is per-visitor
 * session state, not page content; an editor who wants the link CTA turns
 * `apply_now_is_cta` off and sets `apply_now_link`.
 */

interface HeroSlide {
  id?: string | number;
  image?: string;
  alt_image?: string;
  /** Optional; falls back to `image` when empty. */
  mobile_image?: string;
  mobile_alt_image?: string;
  priority?: number;
  status?: boolean;
}

interface Banner1051Data {
  /** Carousel slides. */
  metadata?: HeroSlide[];
  /** Autoplay interval in ms. */
  slide_frequency?: number;
  /** Desktop height in px. 0 (the default) keeps v1's viewport-relative height. */
  hero_section_height?: number;
  /** Eyebrow above the name. */
  title_1?: string;
  title_1_color?: string;
  /** The university name. */
  title?: string;
  title_color?: string;
  /** The coloured line under the name. */
  title_2?: string;
  title_2_color?: string;
  description?: string;
  description_color?: string;
  /** Fill of the card over the slide. */
  background_color?: string;
  apply_now_show?: boolean;
  apply_now_text?: string;
  apply_now_link?: string;
  apply_now_is_cta?: boolean;
  apply_now_color?: string;
  button_color?: string;
  /** Prev / next arrow pill background. */
  carousel_color?: string;
  status?: boolean;
}

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

function sortActive<T extends { priority?: number; status?: boolean }>(
  rows: T[] | undefined,
): T[] {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((r) => r?.status !== false)
    .sort((a, b) => (Number(a?.priority) || 0) - (Number(b?.priority) || 0));
}

const Banner1051 = (props: { data?: Banner1051Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as Banner1051Data | undefined;
  const dispatch = useDispatch();
  const [index, setIndex] = useState(0);

  const {
    slide_frequency = 5000,
    hero_section_height = 0,
    title_1 = "Welcome to",
    title_1_color = "#031F44",
    title = "",
    title_color = "#031F44",
    title_2 = "Online",
    title_2_color = "#9F1C33",
    description = "",
    description_color = "#031F44",
    background_color = "rgba(255, 255, 255, 0.9)",
    apply_now_text = "Enroll Now",
    apply_now_link = "",
    apply_now_color = "#FFFFFF",
    button_color = "#9F1C33",
    carousel_color = "rgba(0, 0, 0, 0.5)",
  } = sectionData || {};

  const applyNowShow = isCmsShowFlagOn(sectionData?.apply_now_show, true);
  const applyNowIsCta = isCmsShowFlagOn(sectionData?.apply_now_is_cta, true);
  const status = isCmsShowFlagOn(sectionData?.status, true);

  const slides = useMemo(
    () => sortActive(sectionData?.metadata).filter((s) => !!(s?.image || s?.mobile_image)),
    [sectionData?.metadata],
  );
  const n = slides.length;

  // v1 advances on a fixed interval with no pause-on-hover.
  useEffect(() => {
    if (!status || n <= 1 || !slide_frequency) return undefined;
    const id = window.setInterval(
      () => setIndex((prev) => (prev === n - 1 ? 0 : prev + 1)),
      Number(slide_frequency),
    );
    return () => window.clearInterval(id);
  }, [status, n, slide_frequency]);

  // A slide count change (CMS edit / preview) can leave `index` past the end.
  useEffect(() => {
    if (n > 0 && index > n - 1) setIndex(0);
  }, [n, index]);

  const prevSlide = useCallback(
    () => setIndex((p) => (p === 0 ? n - 1 : p - 1)),
    [n],
  );
  const nextSlide = useCallback(
    () => setIndex((p) => (p === n - 1 ? 0 : p + 1)),
    [n],
  );

  const handleApplyNowClick = useCallback(() => {
    ctaClick({
      section: "banner_1051",
      component: "hero_apply_now",
      label: apply_now_text?.trim() || "apply_now",
      step: "1",
      entity: "banner_1051",
      parentEntity: "banner_1051",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [apply_now_text, dispatch]);

  if (!status || n === 0) return null;

  const pinnedHeight = Number(hero_section_height) > 0;
  const ctaClassName = cn(
    "mt-10 inline-flex h-[37px] w-[170px] items-center justify-center",
    "whitespace-nowrap rounded-md text-base font-medium shadow transition-colors",
  );
  const ctaStyle = { backgroundColor: button_color, color: apply_now_color };

  return (
    <section
      className={cn(
        "relative w-full overflow-hidden",
        // v1: 60vh below 768px; 75vh above it, or 90vh when the window is short.
        pinnedHeight
          ? "h-[60vh] tab:h-[var(--kuk-hero-h)]"
          : "h-[60vh] tab:h-[75vh] tab:[@media(max-height:799px)]:h-[90vh]",
      )}
      style={
        pinnedHeight
          ? ({ ["--kuk-hero-h" as string]: `${hero_section_height}px` } as any)
          : undefined
      }
    >
      <div
        className="flex size-full transition-transform duration-700 ease-in-out"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {slides.map((slide, i) => {
          const desktopSrc = slide.image || slide.mobile_image || "";
          const mobileSrc = slide.mobile_image || slide.image || "";
          const isFirst = i === 0;

          return (
            <div key={slide.id ?? i} className="relative size-full shrink-0">
              <Image
                src={desktopSrc}
                alt={slide.alt_image || `Slide ${i + 1}`}
                width={1920}
                height={900}
                priority={isFirst}
                loading={isFirst ? "eager" : "lazy"}
                quality={85}
                fetchPriority={isFirst ? "high" : "auto"}
                sizes="100vw"
                className="hidden size-full object-cover object-top tab:block"
              />
              <Image
                src={mobileSrc}
                alt={slide.mobile_alt_image || slide.alt_image || `Slide ${i + 1}`}
                width={800}
                height={600}
                priority={isFirst}
                loading={isFirst ? "eager" : "lazy"}
                quality={85}
                fetchPriority={isFirst ? "high" : "auto"}
                sizes="100vw"
                className="size-full object-cover object-top tab:hidden"
              />
            </div>
          );
        })}
      </div>

      {/* v1 pins the card to the carousel, not to a slide — it does not move. */}
      <div
        className={cn(
          "absolute left-0 top-20 h-[70%] w-3/4",
          "tab:left-20 tab:top-0 tab:h-full tab:w-72",
          "sm:w-[35%]",
        )}
        style={{ backgroundColor: background_color }}
      >
        <div
          className={cn(
            "flex h-full items-center justify-center px-6 pb-0 pt-6",
            "tab:px-8",
            "sm:ml-10 sm:w-72 sm:px-0",
          )}
        >
          <div className="text-left">
            {title_1 && (
              <p
                className="mb-2 text-sm font-bold tab:mb-3 tab:text-lg sm:text-xl"
                style={{ color: title_1_color }}
              >
                {title_1}
              </p>
            )}
            {title && (
              <h1
                className="text-3xl font-bold sm:leading-[50px]"
                style={{ color: title_color }}
              >
                {title}
              </h1>
            )}
            {title_2 && (
              <p
                className="text-3xl font-bold sm:mt-1"
                style={{ color: title_2_color }}
              >
                {title_2}
              </p>
            )}
            {description && (
              <p
                className={cn(
                  "mt-4 text-xs font-normal",
                  "tab:text-[12px] tab:leading-7",
                  "sm:text-base",
                )}
                style={{ color: description_color }}
              >
                {description}
              </p>
            )}
            {applyNowShow && apply_now_text && (
              <>
                {applyNowIsCta || !apply_now_link ? (
                  <button
                    type="button"
                    onClick={handleApplyNowClick}
                    className={ctaClassName}
                    style={ctaStyle}
                  >
                    {apply_now_text}
                  </button>
                ) : (
                  <Link
                    href={normalizeHref(apply_now_link)}
                    className={ctaClassName}
                    style={ctaStyle}
                  >
                    {apply_now_text}
                  </Link>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* v1 shows the arrows from 768px up and only with more than one slide. */}
      {n > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous slide"
            onClick={prevSlide}
            className={cn(
              "absolute left-5 top-1/2 z-50 hidden -translate-y-1/2 rounded-full p-2",
              "text-white transition tab:block",
            )}
            style={{ backgroundColor: carousel_color }}
          >
            &#10094;
          </button>
          <button
            type="button"
            aria-label="Next slide"
            onClick={nextSlide}
            className={cn(
              "absolute right-5 top-1/2 z-50 hidden -translate-y-1/2 rounded-full p-2",
              "text-white transition tab:block",
            )}
            style={{ backgroundColor: carousel_color }}
          >
            &#10095;
          </button>
        </>
      )}
    </section>
  );
};

export default Banner1051;
